import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { GeographicDataList } from '../api/dashboard.schemas';

interface MapProps {
  data: GeographicDataList;
  loading?: boolean;
  error?: Error;
  height?: number;
  className?: string;
  title?: string;
}

/**
 * Map Component
 * 
 * Renders a geographic scatter plot showing validation distribution by country.
 * Uses ECharts with world map visualization and responsive sizing.
 */
export function Map({ 
  data, 
  loading = false, 
  error, 
  height = 400,
  className = "",
  title = "Geographic Distribution"
}: MapProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current || loading || error) return;

    // Check if we have valid data
    if (!data || data.length === 0) return;

    // Initialize chart instance
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const chart = chartInstance.current;

    try {
      // Prepare data for scatter plot (since we don't have world map JSON)
      const scatterData = data
        .filter(item => 
          item.coordinates && 
          typeof item.coordinates.lat === 'number' && 
          typeof item.coordinates.lng === 'number' &&
          !isNaN(item.coordinates.lat) && 
          !isNaN(item.coordinates.lng) &&
          item.coordinates.lat >= -90 && item.coordinates.lat <= 90 &&
          item.coordinates.lng >= -180 && item.coordinates.lng <= 180
        )
        .map(item => ({
          name: item.country || 'Unknown',
          value: [item.coordinates.lng, item.coordinates.lat, item.validations || 0],
          itemStyle: {
            color: getColorByValue(item.validations || 0, data),
          },
        }));

      // If no valid data after filtering, don't render chart
      if (scatterData.length === 0) {
        return;
      }

      // Get max value for color scaling
      const maxValidations = Math.max(...data.map(item => item.validations || 0));

      // Configure chart options for geographic scatter plot
      const option: echarts.EChartsOption = {
        title: {
          text: title,
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 'normal',
          },
        },
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            if (params.data) {
              const [lng, lat, validations] = params.data.value;
              return `${params.data.name}<br/>Validations: ${validations.toLocaleString()}<br/>Coordinates: ${lat.toFixed(2)}, ${lng.toFixed(2)}`;
            }
            return '';
          },
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          top: 'bottom',
          data: ['Validations by Country'],
        },
        geo: {
          map: 'world',
          roam: true,
          zoom: 1.2,
          center: [0, 20],
          itemStyle: {
            borderColor: '#404a59',
            borderWidth: 0.5,
            areaColor: '#f3f3f3',
          },
          emphasis: {
            itemStyle: {
              areaColor: '#e6e6e6',
            },
          },
        },
        series: [
          {
            name: 'Validations by Country',
            type: 'scatter',
            coordinateSystem: 'geo',
            data: scatterData,
            symbolSize: (val: number[]) => {
              // Scale symbol size based on validation count
              const validations = val[2];
              const minSize = 8;
              const maxSize = 40;
              const ratio = maxValidations > 0 ? validations / maxValidations : 0;
              return minSize + (maxSize - minSize) * ratio;
            },
            label: {
              show: false,
              formatter: '{b}',
              position: 'right',
            },
            emphasis: {
              label: {
                show: true,
              },
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
          },
        ],
        visualMap: {
          min: 0,
          max: maxValidations,
          left: 'left',
          top: 'top',
          text: ['High', 'Low'],
          calculable: true,
          inRange: {
            color: ['#e0f3ff', '#006edd'],
          },
        },
        responsive: true,
      };

      chart.setOption(option);

      // Handle resize
      const handleResize = () => {
        chart.resize();
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    } catch (chartError) {
      console.error('Error rendering map chart:', chartError);
    }
  }, [data, loading, error, title]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  // Helper function to get color based on validation count
  function getColorByValue(value: number, allData: GeographicDataList): string {
    if (!allData || allData.length === 0) return '#e0f3ff';
    
    const validValues = allData.map(item => item.validations || 0).filter(v => v > 0);
    if (validValues.length === 0) return '#e0f3ff';
    
    const max = Math.max(...validValues);
    const min = Math.min(...validValues);
    const ratio = max > min ? (value - min) / (max - min) : 0;
    
    // Color gradient from light blue to dark blue
    const r = Math.round(224 - (224 - 0) * ratio);
    const g = Math.round(243 - (243 - 110) * ratio);
    const b = Math.round(255 - (255 - 221) * ratio);
    
    return `rgb(${r}, ${g}, ${b})`;
  }

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>
            <div className="h-4 bg-muted animate-pulse rounded w-1/2" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div 
            className="flex items-center justify-center bg-muted animate-pulse rounded"
            style={{ height }}
          >
            <div className="text-muted-foreground">Loading map...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-base">Map Error</CardTitle>
          <CardDescription>Failed to load geographic data</CardDescription>
        </CardHeader>
        <CardContent>
          <div 
            className="flex items-center justify-center border-2 border-dashed border-muted rounded"
            style={{ height }}
          >
            <div className="text-center text-muted-foreground">
              <p>Unable to display map</p>
              <p className="text-sm">{error.message}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription>No geographic data available</CardDescription>
        </CardHeader>
        <CardContent>
          <div 
            className="flex items-center justify-center border-2 border-dashed border-muted rounded"
            style={{ height }}
          >
            <div className="text-center text-muted-foreground">
              <p>No data to display</p>
              <p className="text-sm">Geographic distribution will appear when data is available</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardContent className="p-6">
        <div 
          ref={chartRef} 
          style={{ height, width: '100%' }}
          className="min-h-0"
        />
      </CardContent>
    </Card>
  );
}